import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import {
  SocialContact,
  SocialContactWithDetails,
  SocialMessage,
  SocialInboxFilters,
  UseSocialInboxResult
} from '@/types/social';
import { toast } from 'sonner';

export function useSocialInbox(businessId: string | null): UseSocialInboxResult {
  const queryClient = useQueryClient();
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [filters, setFilters] = useState<SocialInboxFilters>({});

  const queryKey = ['social-inbox', businessId, filters];

  // Fetch conversations (contacts with last message and unread count)
  const {
    data: conversations = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      // Fetch contacts
      let query = supabase
        .from('social_contacts')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_blocked', false)
        .order('last_inbound_at', { ascending: false, nullsFirst: false });

      // Apply platform filter
      if (filters.platform) {
        query = query.eq('platform', filters.platform);
      }

      // Apply search filter
      if (filters.search) {
        query = query.or(`username.ilike.%${filters.search}%,display_name.ilike.%${filters.search}%`);
      }

      const { data: contacts, error: contactsError } = await query;
      if (contactsError) throw contactsError;

      if (!contacts || contacts.length === 0) return [];

      // Fetch unread counts and last messages for each contact
      const contactsWithDetails: SocialContactWithDetails[] = await Promise.all(
        contacts.map(async (contact) => {
          // Get unread count
          const { count: unreadCount } = await supabase
            .from('social_messages')
            .select('*', { count: 'exact', head: true })
            .eq('contact_id', contact.id)
            .eq('direction', 'inbound')
            .is('read_at', null);

          // Get last message
          const { data: lastMessages } = await supabase
            .from('social_messages')
            .select('*')
            .eq('contact_id', contact.id)
            .order('sent_at', { ascending: false })
            .limit(1);

          // Get associated account
          const { data: accountData } = await supabase
            .from('social_accounts')
            .select('*')
            .eq('business_id', businessId)
            .eq('platform', contact.platform)
            .eq('status', 'active')
            .single();

          return {
            ...contact,
            unread_count: unreadCount || 0,
            last_message: lastMessages?.[0] as SocialMessage | undefined,
            account: accountData || undefined
          } as SocialContactWithDetails;
        })
      );

      // Apply unread filter after fetching
      if (filters.unreadOnly) {
        return contactsWithDetails.filter(c => c.unread_count > 0);
      }

      return contactsWithDetails;
    },
    enabled: !!businessId,
  });

  // Selected conversation
  const selectedConversation = conversations.find(c => c.id === selectedContactId) || null;

  // Subscribe to realtime updates
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`social-inbox-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_messages',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_contacts',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  // Select conversation
  const selectConversation = useCallback((contactId: string | null) => {
    setSelectedContactId(contactId);
  }, []);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('social_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('contact_id', contactId)
        .eq('direction', 'inbound')
        .is('read_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Block contact mutation
  const blockContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('social_contacts')
        .update({ is_blocked: true })
        .eq('id', contactId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Contact blocked');
    },
    onError: (error: Error) => {
      toast.error('Failed to block contact', { description: error.message });
    },
  });

  // Unblock contact mutation
  const unblockContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('social_contacts')
        .update({ is_blocked: false })
        .eq('id', contactId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Contact unblocked');
    },
    onError: (error: Error) => {
      toast.error('Failed to unblock contact', { description: error.message });
    },
  });

  const markAsRead = useCallback(async (contactId: string) => {
    await markAsReadMutation.mutateAsync(contactId);
  }, [markAsReadMutation]);

  const blockContact = useCallback(async (contactId: string) => {
    await blockContactMutation.mutateAsync(contactId);
  }, [blockContactMutation]);

  const unblockContact = useCallback(async (contactId: string) => {
    await unblockContactMutation.mutateAsync(contactId);
  }, [unblockContactMutation]);

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<SocialInboxFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  return {
    conversations,
    isLoading,
    error: error as Error | null,
    selectedConversation,
    selectConversation,
    markAsRead,
    blockContact,
    unblockContact,
    refresh,
    filters,
    updateFilters,
  };
}

export default useSocialInbox;
